import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/state/auth/AuthProvider';
import { GenericCRUDService } from '@/services/genericCRUD';
import { tenantEntity } from '@/config/entities/tenants';
import { FormData } from '@/types/crud';
import { toast } from 'react-hot-toast';
import api from '@/services/api';
import { Modal } from '@/components/ui/Modal';
import { TENANT_STATUS_OPTIONS } from '@/types/enums';
import { ShieldCheckIcon, TrashIcon } from '@heroicons/react/24/outline';

type TenantUser = { id: string; email: string; first_name: string; last_name: string; status: string; is_tenant_admin: boolean; is_chief_access_officer?: boolean };
type PlatformRole = { id: string; role_name: string; display_name?: string };
type RoleAssignment = { id: string; role_id: string; role_name: string; status: string };

export const TenantFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<FormData>({});
  const [formValues, setFormValues] = useState<{ name: string; domain: string; status: string }>({ name: '', domain: '', status: 'ACTIVE' });

  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [addAdminModalOpen, setAddAdminModalOpen] = useState(false);
  const [addAdminSelectedId, setAddAdminSelectedId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const [assignRolesModalOpen, setAssignRolesModalOpen] = useState(false);
  const [assignRolesUser, setAssignRolesUser] = useState<TenantUser | null>(null);
  const [availableRoles, setAvailableRoles] = useState<PlatformRole[]>([]);
  const [, setUserAssignments] = useState<RoleAssignment[]>([]);
  const [roleCheckboxes, setRoleCheckboxes] = useState<Record<string, boolean>>({});
  const [assignRolesSaving, setAssignRolesSaving] = useState(false);

  const crudService = new GenericCRUDService(tenantEntity);
  const tenantAdmins = tenantUsers.filter((u) => u.is_tenant_admin);
  const canRemoveAdmin = tenantAdmins.length > 1;

  useEffect(() => {
    if (isEditing && id) {
      loadTenant();
    }
  }, [id, isEditing]);

  const loadTenant = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const tenant = await crudService.getById(id);
      setInitialData(tenant);
      setFormValues({
        name: (tenant.name as string) ?? '',
        domain: (tenant.domain as string) ?? '',
        status: (tenant.status as string) ?? 'ACTIVE',
      });
    } catch (err) {
      toast.error(t('tenantManagement.failedToLoadTenant'));
      navigate('/tenants');
    } finally {
      setLoading(false);
    }
  };

  const loadTenantUsers = async () => {
    if (!id) return;
    setUsersError(null);
    setUsersLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: TenantUser[] }>(`/tenants/${id}/users`);
      const data = res.data?.data ?? [];
      setTenantUsers(data);
    } catch (err: any) {
      setUsersError(err.response?.data?.message ?? t('tenantManagement.failedToLoadTenantUsers'));
      setTenantUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (isEditing && id) loadTenantUsers();
  }, [isEditing, id]);

  const handleSaveTenant = async () => {
    try {
      if (isEditing && id) {
        await crudService.update(id, { ...formValues });
        toast.success(t('tenantManagement.tenantUpdatedSuccessfully'));
      } else {
        await crudService.create({ ...formValues });
        toast.success(t('tenantManagement.tenantCreatedSuccessfully'));
      }
      navigate('/tenants');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleCancel = () => navigate('/tenants');

  const handleAddAdminOpen = () => {
    setAddAdminSelectedId(nonAdminUsers[0]?.id ?? '');
    setAddAdminModalOpen(true);
  };

  const handleAddAdmin = async () => {
    if (!id || !addAdminSelectedId) {
      toast.error(t('tenantManagement.selectUser'));
      return;
    }
    setAssigning(true);
    try {
      await api.post(`/tenants/${id}/assign-admin`, { userId: addAdminSelectedId });
      toast.success(t('tenantManagement.tenantAdminAdded'));
      setAddAdminModalOpen(false);
      await loadTenant();
      await loadTenantUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? 'Failed to add tenant admin');
    } finally {
      setAssigning(false);
    }
  };

  const handleRevokeAdmin = async (userId: string) => {
    if (!id) return;
    setRevokingId(userId);
    try {
      await api.post(`/tenants/${id}/revoke-admin`, { userId });
      toast.success(t('tenantManagement.tenantAdminRemoved'));
      await loadTenant();
      await loadTenantUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? t('tenantManagement.failedToRemoveTenantAdmin'));
    } finally {
      setRevokingId(null);
    }
  };

  const handleAssignRolesOpen = async (user: TenantUser) => {
    setAssignRolesUser(user);
    setAssignRolesModalOpen(true);
    setAvailableRoles([]);
    setUserAssignments([]);
    setRoleCheckboxes({});
    try {
      const [rolesRes, assignmentsRes] = await Promise.all([
        api.get<PlatformRole[]>('/platform/user-roles'),
        api.get<{ success: boolean; data: RoleAssignment[] }>(`/platform/user-role-assignments?user_id=${user.id}`),
      ]);
      const roles = Array.isArray(rolesRes.data) ? rolesRes.data : [];
      const assignments = assignmentsRes.data?.data ?? [];
      setAvailableRoles(roles);
      setUserAssignments(assignments);
      const checked: Record<string, boolean> = {};
      roles.forEach((r) => {
        checked[r.id] = assignments.some((a) => a.role_id === r.id && a.status === 'ACTIVE');
      });
      setRoleCheckboxes(checked);
    } catch (err) {
      toast.error(t('tenantManagement.failedToLoadRoles'));
      setAssignRolesModalOpen(false);
    }
  };

  const handleAssignRolesSave = async () => {
    if (!assignRolesUser || !currentUser?.id) return;
    setAssignRolesSaving(true);
    try {
      const roleIds = Object.keys(roleCheckboxes).filter((rid) => roleCheckboxes[rid]);
      await api.put(`/users/admin/users/${assignRolesUser.id}/provisioning`, { roleIds });
      toast.success(t('tenantManagement.rolesUpdated'));
      setAssignRolesModalOpen(false);
      setAssignRolesUser(null);
      await loadTenantUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? t('tenantManagement.failedToUpdateRoles'));
    } finally {
      setAssignRolesSaving(false);
    }
  };

  const nonAdminUsers = tenantUsers.filter(
    (u) => !u.is_tenant_admin && !(u.is_chief_access_officer ?? false)
  );

  if (loading && isEditing) {
    return (
      <div className="p-6 text-[var(--color-surface-text)]">
        <div className="text-[var(--color-secondary-text)]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 text-[var(--color-surface-text)]">
      <h1 className="text-2xl font-bold text-[var(--color-surface-text)]">
        {isEditing ? t('tenantManagement.editTenant') : t('tenantManagement.createTenant')}
      </h1>

      {/* One row: Company Name *, Domain, Status, Date created */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 items-end border border-[var(--color-border)] rounded-lg p-4 bg-[var(--color-surface-bg)]">
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
            {t(tenantEntity.fields.find((f) => f.key === 'name')?.label ?? 'Company Name')} *
          </label>
          <input
            type="text"
            value={formValues.name}
            onChange={(e) => setFormValues((v) => ({ ...v, name: e.target.value }))}
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] px-3 py-2"
            placeholder={t('tenantManagement.enterCompanyName') ?? 'Company name'}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
            {t(tenantEntity.fields.find((f) => f.key === 'domain')?.label ?? 'Domain')}
          </label>
          <input
            type="text"
            value={formValues.domain}
            onChange={(e) => setFormValues((v) => ({ ...v, domain: e.target.value }))}
            readOnly={isEditing}
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] px-3 py-2 read-only:opacity-70"
            placeholder={t('tenantManagement.enterDomainOptional') ?? 'Domain (optional)'}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
            {t(tenantEntity.fields.find((f) => f.key === 'status')?.label ?? 'Status')}
          </label>
          <select
            value={formValues.status}
            onChange={(e) => setFormValues((v) => ({ ...v, status: e.target.value }))}
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] px-3 py-2"
          >
            {TENANT_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{t(o.label) ?? o.label}</option>
            ))}
          </select>
        </div>
        {isEditing && initialData.created_at ? (
          <div>
            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
              {t('tenantManagement.dateCreated') ?? 'Date created'}
            </label>
            <div className="rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] px-3 py-2 text-sm">
              {new Date(String(initialData.created_at)).toLocaleDateString()}
            </div>
          </div>
        ) : null}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSaveTenant}
            className="rounded bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 text-sm font-medium hover:opacity-90 border border-[var(--color-border)]"
          >
            {isEditing ? t('tenantManagement.updateTenant') : t('tenantManagement.createTenant')}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="rounded border border-[var(--color-border)] text-[var(--color-surface-text)] px-4 py-2 text-sm hover:bg-[var(--color-hover-bg)]"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>

      {isEditing && (
        <>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleAddAdminOpen}
              className="rounded bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 text-sm font-medium hover:opacity-90 border border-[var(--color-border)]"
            >
              {t('tenantManagement.addTenantAdmins')}
            </button>
          </div>

          <div className="border border-[var(--color-border)] rounded-lg overflow-hidden bg-[var(--color-surface-bg)]">
            <div className="px-4 py-2 border-b border-[var(--color-border)] font-medium text-[var(--color-surface-text)]">
              {t('tenantManagement.tenantAdministrators')}
            </div>
            {usersError && (
              <div className="p-4 text-[var(--color-error)] text-sm">{usersError}</div>
            )}
            {!usersError && usersLoading && (
              <div className="p-4 text-sm text-[var(--color-secondary-text)]">{t('common.loading')}</div>
            )}
            {!usersError && !usersLoading && tenantAdmins.length === 0 && (
              <div className="p-4 text-sm text-[var(--color-secondary-text)]">{t('tenantManagement.noTenantAdministrators')}</div>
            )}
            {!usersError && !usersLoading && tenantAdmins.length > 0 && (
              <ul className="divide-y divide-[var(--color-border)]">
                {tenantAdmins.map((u) => (
                  <li key={u.id} className="flex items-center justify-between gap-4 px-4 py-3">
                    <span className="text-sm text-[var(--color-surface-text)]">
                      {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
                      <span className="text-[var(--color-secondary-text)]"> ({u.email})</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleAssignRolesOpen(u)}
                        className="inline-flex items-center gap-1.5 rounded border border-[var(--color-border)] text-[var(--color-surface-text)] px-3 py-1.5 text-sm hover:bg-[var(--color-hover-bg)]"
                      >
                        <ShieldCheckIcon className="h-4 w-4" />
                        {t('tenantManagement.assignRoles')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRevokeAdmin(u.id)}
                        disabled={!canRemoveAdmin || revokingId === u.id}
                        className="inline-flex items-center gap-1.5 rounded border border-[var(--color-error)] text-[var(--color-error)] px-3 py-1.5 text-sm hover:bg-[var(--color-error)]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <TrashIcon className="h-4 w-4" />
                        {revokingId === u.id ? t('tenantManagement.removing') : t('common.delete')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {/* Add tenant admin modal */}
      <Modal
        isOpen={addAdminModalOpen}
        onClose={() => setAddAdminModalOpen(false)}
        title={t('tenantManagement.addTenantAdmin')}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-secondary-text)]">
            {t('tenantManagement.addTenantAdminDescription')}
          </p>
          {nonAdminUsers.length === 0 ? (
            <p className="text-sm text-[var(--color-secondary-text)]">{t('tenantManagement.noUsersAvailableToAdd')}</p>
          ) : (
            <>
              <label className="block text-sm font-medium text-[var(--color-surface-text)]">{t('userManagement.name')}</label>
              <select
                value={addAdminSelectedId}
                onChange={(e) => setAddAdminSelectedId(e.target.value)}
                className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] px-3 py-2"
              >
                {nonAdminUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email} ({u.email})
                  </option>
                ))}
              </select>
            </>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setAddAdminModalOpen(false)}
              className="rounded border border-[var(--color-border)] text-[var(--color-surface-text)] px-4 py-2 text-sm hover:bg-[var(--color-hover-bg)]"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleAddAdmin}
              disabled={assigning || nonAdminUsers.length === 0}
              className="rounded bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 text-sm font-medium border border-[var(--color-border)] disabled:opacity-50"
            >
              {assigning ? t('tenantManagement.adding') : t('common.add')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Assign roles modal */}
      <Modal
        isOpen={assignRolesModalOpen}
        onClose={() => { setAssignRolesModalOpen(false); setAssignRolesUser(null); }}
        title={assignRolesUser ? `${t('tenantManagement.assignRolesForUser')} — ${assignRolesUser.first_name} ${assignRolesUser.last_name} (${assignRolesUser.email})` : t('tenantManagement.assignRolesForUser')}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-secondary-text)]">{t('tenantManagement.selectPlatformRoles')}</p>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {availableRoles.map((role) => (
              <label key={role.id} className="flex items-center gap-2 cursor-pointer text-[var(--color-surface-text)]">
                <input
                  type="checkbox"
                  checked={roleCheckboxes[role.id] ?? false}
                  onChange={(e) => setRoleCheckboxes((prev) => ({ ...prev, [role.id]: e.target.checked }))}
                  className="rounded border-[var(--color-border)] accent-[var(--color-primary-text)]"
                />
                <span className="text-sm">{role.display_name || role.role_name}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => { setAssignRolesModalOpen(false); setAssignRolesUser(null); }}
              className="rounded border border-[var(--color-border)] text-[var(--color-surface-text)] px-4 py-2 text-sm hover:bg-[var(--color-hover-bg)]"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleAssignRolesSave}
              disabled={assignRolesSaving}
              className="rounded bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 text-sm font-medium border border-[var(--color-border)] disabled:opacity-50"
            >
              {assignRolesSaving ? t('tenantManagement.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
